import React from "react";
import PropTypes from "prop-types";
import { Checkbox, IconButton, Tooltip } from "@material-ui/core";
import AssignmentLateOutlinedIcon from "@material-ui/icons/AssignmentLateOutlined";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";

const CellContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  ${({ highlighted }) =>
    highlighted &&
    css`
      background-color: #fff8e1;
      border-radius: 4px;
    `}
`;

const JustifyButton = styled(IconButton)`
  && {
    position: absolute;
    right: 2px;
    top: 2px;
    padding: 4px;
    ${({ justified }) =>
      justified
        ? css`
            color: #2e7d32;
          `
        : css`
            opacity: 0;
            pointer-events: none;
          `}
  }

  ${CellContainer}:hover && {
    opacity: 1;
    pointer-events: auto;
  }
`;

const JustifiedMarker = styled(AssignmentTurnedInIcon)`
  && {
    position: absolute;
    right: 6px;
    top: 6px;
    font-size: 18px;
    color: #2e7d32;
  }
`;

const stopEvent = event => {
  event.preventDefault();
  event.stopPropagation();
};

const FrequencyAttendanceCell = ({
  cellId,
  checked,
  disabled,
  highlighted,
  canJustify,
  justification,
  onCheckboxClick,
  onJustifyClick
}) => {
  const { t } = useTranslation();
  const isAbsent = !checked;
  const isJustified = isAbsent && !!justification;

  const justifiedTitle = isJustified
    ? t("frequency.justify.justifiedTooltip", {
        reason: justification.descricao || t("frequency.justify.unknownReason"),
        type: t(
          justification.abona
            ? "frequency.justify.excused"
            : "frequency.justify.notExcused"
        )
      })
    : "";

  return (
    <CellContainer data-id={cellId} highlighted={highlighted}>
      <Checkbox
        id={cellId}
        checked={checked}
        disabled={disabled}
        onClick={onCheckboxClick}
        color="primary"
      />
      {isAbsent && canJustify && (
        <Tooltip
          title={isJustified ? justifiedTitle : t("frequency.justify.action")}
          placement="top"
        >
          <JustifyButton
            size="small"
            color="primary"
            justified={isJustified ? 1 : 0}
            aria-label={t("frequency.justify.action")}
            onClick={event => {
              stopEvent(event);
              onJustifyClick(event);
            }}
            onMouseDown={stopEvent}
          >
            {isJustified ? (
              <AssignmentTurnedInIcon fontSize="small" />
            ) : (
              <AssignmentLateOutlinedIcon fontSize="small" />
            )}
          </JustifyButton>
        </Tooltip>
      )}
      {isJustified && !canJustify && (
        <Tooltip title={justifiedTitle} placement="top">
          <JustifiedMarker aria-label={justifiedTitle} />
        </Tooltip>
      )}
    </CellContainer>
  );
};

FrequencyAttendanceCell.propTypes = {
  cellId: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  highlighted: PropTypes.bool,
  canJustify: PropTypes.bool,
  justification: PropTypes.shape({
    descricao: PropTypes.string,
    abona: PropTypes.bool
  }),
  onCheckboxClick: PropTypes.func.isRequired,
  onJustifyClick: PropTypes.func.isRequired
};

FrequencyAttendanceCell.defaultProps = {
  disabled: false,
  highlighted: false,
  canJustify: false,
  justification: null
};

export default FrequencyAttendanceCell;
